import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Standalone chrome for the public (no-login) legal pages: privacy, terms,
 * and data deletion. Deliberately self-contained so it renders outside the
 * authenticated AppLayout.
 */
export const LegalLayout: React.FC<{
  title: string;
  updated?: string;
  children: React.ReactNode;
}> = ({ title, updated = 'August 2026', children }) => {
  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <Link to="/login" style={styles.wordmark}>VERA</Link>
        <nav style={styles.nav}>
          <Link to="/privacy" style={styles.navLink}>Privacy</Link>
          <Link to="/terms" style={styles.navLink}>Terms</Link>
          <Link to="/data-deletion" style={styles.navLink}>Data Deletion</Link>
        </nav>
      </header>

      <main style={styles.card}>
        <h1 style={styles.title}>{title}</h1>
        <p style={styles.updated}>Last updated: {updated}</p>
        <div style={styles.body}>{children}</div>
        <footer style={styles.footer}>
          VERA Health Technologies, LLC · Contact:{' '}
          <a href="mailto:support@veraaba.com" style={styles.link}>support@veraaba.com</a>
        </footer>
      </main>
    </div>
  );
};

/** Shared section heading for legal copy. */
export const LegalSection: React.FC<{ heading: string; children: React.ReactNode }> = ({
  heading,
  children,
}) => (
  <section style={{ marginTop: 28 }}>
    <h2 style={styles.h2}>{heading}</h2>
    <div style={styles.sectionBody}>{children}</div>
  </section>
);

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#F5F0EB',
    color: '#2D2D2D',
    fontFamily:
      "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
    padding: '24px 16px 64px',
  },
  header: {
    maxWidth: 820,
    margin: '0 auto 24px',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  wordmark: {
    fontWeight: 800,
    fontSize: 28,
    letterSpacing: '0.06em',
    color: '#800020',
    textDecoration: 'none',
  },
  nav: { display: 'flex', gap: 18, flexWrap: 'wrap' },
  navLink: { color: '#4F6367', textDecoration: 'none', fontSize: 14, fontWeight: 600 },
  card: {
    maxWidth: 820,
    margin: '0 auto',
    background: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #E5E0DB',
    padding: '32px clamp(20px, 5vw, 48px)',
    boxShadow: '0 2px 12px rgba(0,0,0,0.05)',
  },
  title: { fontSize: 28, fontWeight: 700, margin: 0, color: '#2D2D2D' },
  updated: { fontSize: 13, color: '#6B6B6B', marginTop: 6 },
  body: { fontSize: 15, lineHeight: 1.7 },
  h2: { fontSize: 19, fontWeight: 700, color: '#4F6367', margin: 0 },
  sectionBody: { marginTop: 8, color: '#3d3d3d' },
  footer: {
    marginTop: 40,
    paddingTop: 20,
    borderTop: '1px solid #E5E0DB',
    fontSize: 13,
    color: '#6B6B6B',
  },
  link: { color: '#4F6367', fontWeight: 600 },
};
